// Portal Settings - For teachers to customize their enrollment portal
import { useState, useEffect } from 'react';
import { Save, Upload, Eye, Settings, Monitor, Plus, Pencil, Trash2, FileText, Globe, Layout, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import pagesService from '../../services/pagesService';
import { formatPhoneInput } from '../../utils/formatters';

const PortalSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [settings, setSettings] = useState({
    logo: null,
    logoFile: null,
    logoSize: 'medium',
    logoIndent: 0,
    businessName: '',
    tagline: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    showPublicClasses: true,
    allowPublicEnrollment: true,
    requireParentInfo: true,
    requireMedicalInfo: false,
    navigationLinks: [
      { id: 'about', label: 'About', url: '#about', enabled: true },
      { id: 'tutorials', label: 'Tutorials', url: '#tutorials', enabled: true },
      { id: 'home', label: 'Home', url: '#home', enabled: true },
      { id: 'contact', label: 'Contact', url: '#contact', enabled: true },
      { id: 'calendar', label: 'Calendar', url: '#calendar', enabled: true }
    ],
    // Header settings
    headerBackgroundColor: '#ffffff',
    headerHeight: 'medium',
    headerLayout: 'standard',
    // Social media settings
    showSocialMedia: false,
    socialMedia: {
      facebook: { enabled: false, url: '' },
      instagram: { enabled: false, url: '' },
      twitter: { enabled: false, url: '' },
      youtube: { enabled: false, url: '' },
      pinterest: { enabled: false, url: '' },
      tiktok: { enabled: false, url: '' },
      linkedin: { enabled: false, url: '' }
    }
  });

  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [liveUrl, setLiveUrl] = useState('');
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    fetchSettings();
    fetchPages();
    generatePreviewUrl();
    fetchTeacherInfo();
  }, []);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Intercept navigation clicks
  useEffect(() => {
    const handleClick = (e) => {
      if (!hasUnsavedChanges) return;

      // Check if the click is on a link that would navigate away
      const link = e.target.closest('a[href]');
      if (link && link.href) {
        const url = new URL(link.href);
        const isInternal = url.origin === window.location.origin;
        const isSamePage = url.pathname === location.pathname;

        if (isInternal && !isSamePage) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNavigation(url.pathname);
          setShowNavigationWarning(true);
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasUnsavedChanges, location.pathname]);

  const handleNavigationConfirm = () => {
    setShowNavigationWarning(false);
    if (pendingNavigation) {
      setHasUnsavedChanges(false); // Clear flag before navigating
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleNavigationCancel = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const fetchSettings = async () => {
    try {
      const data = await api.request('/portal/settings');
      // Merge with defaults, especially for nested socialMedia object
      const fetchedSettings = data.settings || {};
      const defaultSocialMedia = {
        facebook: { enabled: false, url: '' },
        instagram: { enabled: false, url: '' },
        twitter: { enabled: false, url: '' },
        youtube: { enabled: false, url: '' },
        pinterest: { enabled: false, url: '' },
        tiktok: { enabled: false, url: '' },
        linkedin: { enabled: false, url: '' }
      };

      // Merge fetched socialMedia with defaults
      const mergedSocialMedia = { ...defaultSocialMedia };
      if (fetchedSettings.socialMedia && typeof fetchedSettings.socialMedia === 'object') {
        Object.keys(defaultSocialMedia).forEach(platform => {
          if (fetchedSettings.socialMedia[platform]) {
            mergedSocialMedia[platform] = {
              ...defaultSocialMedia[platform],
              ...fetchedSettings.socialMedia[platform]
            };
          }
        });
      }

      setSettings(prev => ({
        ...prev,
        ...fetchedSettings,
        socialMedia: mergedSocialMedia
      }));
    } catch (error) {
      console.error('Error fetching portal settings:', error);
    }
  };

  const fetchPages = async () => {
    try {
      setLoadingPages(true);
      const data = await pagesService.getPages();
      setPages(data.pages || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoadingPages(false);
    }
  };

  const fetchTeacherInfo = async () => {
    try {
      const storedUser = localStorage.getItem('classtraks_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        setTeacherInfo({
          first_name: user.firstName,
          last_name: user.lastName,
          email: user.email
        });
      }
    } catch (error) {
      console.error('Error fetching teacher info:', error);
    }
  };

  const generatePreviewUrl = () => {
    try {
      const storedUser = localStorage.getItem('classtraks_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        const userId = user.id;
        setPreviewUrl(`${window.location.origin}/portal/${userId}/home`);
        setLiveUrl(`${window.location.origin}/portal/${userId}/home`);
      } else {
        setPreviewUrl('');
        setLiveUrl('');
      }
    } catch (error) {
      console.error('Error generating preview URL:', error);
      setPreviewUrl('');
      setLiveUrl('');
    }
  };

  // Helper function to update settings and mark as unsaved
  const updateSettings = (updater) => {
    setSettings(updater);
    setSaved(false);
    setHasUnsavedChanges(true);
  };

  // Toggle page navigation visibility
  const togglePageNavigation = async (pageId, currentValue) => {
    try {
      await pagesService.updatePage(pageId, { showInNavigation: !currentValue });
      // Update local state
      setPages(prev => prev.map(p =>
        p.id === pageId ? { ...p, show_in_navigation: !currentValue } : p
      ));
    } catch (error) {
      console.error('Error toggling page navigation:', error);
    }
  };

  // Move page up in navigation order
  const movePageUp = async (index) => {
    if (index === 0) return;
    const newPages = [...pages];
    [newPages[index - 1], newPages[index]] = [newPages[index], newPages[index - 1]];

    // Update navigationOrder for swapped pages
    try {
      await Promise.all([
        pagesService.updatePage(newPages[index - 1].id, { navigationOrder: index - 1 }),
        pagesService.updatePage(newPages[index].id, { navigationOrder: index })
      ]);
      setPages(newPages);
    } catch (error) {
      console.error('Error reordering pages:', error);
    }
  };

  // Move page down in navigation order
  const movePageDown = async (index) => {
    if (index === pages.length - 1) return;
    const newPages = [...pages];
    [newPages[index], newPages[index + 1]] = [newPages[index + 1], newPages[index]];

    // Update navigationOrder for swapped pages
    try {
      await Promise.all([
        pagesService.updatePage(newPages[index].id, { navigationOrder: index }),
        pagesService.updatePage(newPages[index + 1].id, { navigationOrder: index + 1 })
      ]);
      setPages(newPages);
    } catch (error) {
      console.error('Error reordering pages:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;

    // Format phone number as user types
    if (name === 'contactPhone') {
      finalValue = formatPhoneInput(value);
    }

    updateSettings(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        updateSettings(prev => ({
          ...prev,
          logoFile: file,
          logo: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Save as draft (does not publish)
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const formData = new FormData();

      // Always save as draft (not publishing)
      formData.append('isPublished', 'false');

      // Add all settings except the file objects and special arrays/objects
      Object.keys(settings).forEach(key => {
        if (key !== 'logoFile' && key !== 'logo' && key !== 'navigationLinks' && key !== 'socialMedia') {
          formData.append(key, settings[key]);
        }
      });

      // Add social media as JSON
      if (settings.socialMedia) {
        formData.append('socialMedia', JSON.stringify(settings.socialMedia));
      }

      // Add logo file if present
      if (settings.logoFile) {
        formData.append('logo', settings.logoFile);
      }

      const response = await fetch(api.baseURL + '/portal/settings', {
        method: 'PUT',
        headers: {
          ...api.getAuthHeaders()
        },
        body: formData
      });

      if (response.ok) {
        setSaved(true);
        setHasUnsavedChanges(false);
        setTimeout(() => setSaved(false), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving portal settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Publish draft to live site
  const handlePublish = async () => {
    try {
      setPublishing(true);

      // First save current changes as draft
      await handleSaveDraft();

      // Then publish the draft
      const response = await api.post('/portal/settings/publish');

      if (response) {
        setShowPublishConfirm(false);
        setShowPublishSuccess(true);
      }
    } catch (error) {
      console.error('Error publishing settings:', error);
      alert('Failed to publish settings: ' + (error.message || 'Please try again.'));
    } finally {
      setPublishing(false);
    }
  };

  const handleCreatePage = async () => {
    if (!newPageTitle.trim()) {
      alert('Please enter a page title');
      return;
    }

    try {
      const pageData = {
        title: newPageTitle.trim(),
        slug: newPageSlug.trim() || newPageTitle.trim().toLowerCase().replace(/\s+/g, '-'),
        is_published: false
      };

      const result = await pagesService.createPage(pageData);
      setPages(prev => [...prev, result.page]);
      setShowNewPageModal(false);
      setNewPageTitle('');
      setNewPageSlug('');

      // Navigate to the page builder for the new page
      navigate(`/pages/${result.page.id}/edit`);
    } catch (error) {
      console.error('Error creating page:', error);
      alert('Failed to create page. Please try again.');
    }
  };

  const handleDeletePage = async (pageId, pageTitle) => {
    if (!confirm(`Are you sure you want to delete "${pageTitle}"? This cannot be undone.`)) {
      return;
    }

    try {
      await pagesService.deletePage(pageId);
      setPages(prev => prev.filter(p => p.id !== pageId));
    } catch (error) {
      console.error('Error deleting page:', error);
      alert('Failed to delete page. Please try again.');
    }
  };

  // Generate slug from title
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setNewPageTitle(title);
    // Auto-generate slug from title
    setNewPageSlug(title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Globe className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-white">Portal Settings</h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">Customize your public enrollment portal for students and parents</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Save Draft Button */}
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                saved
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } disabled:bg-blue-400`}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Draft'}
            </button>

            {/* Preview Draft Button */}
            <a
              href={`${previewUrl}?preview=true`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Draft
            </a>

            {/* Publish Button */}
            <button
              onClick={() => setShowPublishConfirm(true)}
              disabled={publishing}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:bg-purple-400"
            >
              <Upload className="w-4 h-4 mr-2" />
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6">
        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-2">
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-3 px-5 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'settings'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Site Settings
              </div>
            </button>
            <button
              onClick={() => setActiveTab('pages')}
              className={`py-3 px-5 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'pages'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center">
                <Layout className="w-4 h-4 mr-2" />
                Site Pages
              </div>
            </button>
          </nav>
        </div>

        <div className="space-y-8">

          {/* Sitewide Settings Section */}
          {activeTab === 'settings' && (
          <div className="space-y-6">

            {/* Header Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Header</h3>
              </div>

              <div className="space-y-6">
                {/* Header Layout Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Layout Style
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {/* Standard Layout */}
                    <button
                      type="button"
                      onClick={() => updateSettings(prev => ({ ...prev, headerLayout: 'standard' }))}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        settings.headerLayout === 'standard' || !settings.headerLayout
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 p-2 mb-2">
                        <div className="flex items-center justify-between">
                          <div className="w-6 h-6 bg-gray-300 dark:bg-gray-500 rounded"></div>
                          <div className="flex gap-1">
                            <div className="w-4 h-1 bg-gray-300 dark:bg-gray-500 rounded"></div>
                            <div className="w-4 h-1 bg-gray-300 dark:bg-gray-500 rounded"></div>
                            <div className="w-4 h-1 bg-gray-300 dark:bg-gray-500 rounded"></div>
                          </div>
                          <div className="flex gap-0.5">
                            <div className="w-2 h-2 bg-gray-300 dark:bg-gray-500 rounded-full"></div>
                            <div className="w-2 h-2 bg-gray-300 dark:bg-gray-500 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Standard</span>
                    </button>

                    {/* Centered Layout */}
                    <button
                      type="button"
                      onClick={() => updateSettings(prev => ({ ...prev, headerLayout: 'centered' }))}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        settings.headerLayout === 'centered'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 p-2 mb-2">
                        <div className="flex flex-col items-center">
                          <div className="w-6 h-6 bg-gray-300 dark:bg-gray-500 rounded mb-1"></div>
                          <div className="flex gap-1">
                            <div className="w-3 h-1 bg-gray-300 dark:bg-gray-500 rounded"></div>
                            <div className="w-3 h-1 bg-gray-300 dark:bg-gray-500 rounded"></div>
                            <div className="w-3 h-1 bg-gray-300 dark:bg-gray-500 rounded"></div>
                          </div>
                        </div>
                        <div className="flex justify-end mt-1">
                          <div className="flex gap-0.5">
                            <div className="w-2 h-2 bg-gray-300 dark:bg-gray-500 rounded-full"></div>
                            <div className="w-2 h-2 bg-gray-300 dark:bg-gray-500 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Centered</span>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                  {/* Header Background & Height */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Background Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          name="headerBackgroundColor"
                          value={settings.headerBackgroundColor || '#ffffff'}
                          onChange={handleInputChange}
                          className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings.headerBackgroundColor || '#ffffff'}
                          onChange={(e) => updateSettings(prev => ({ ...prev, headerBackgroundColor: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Header Height
                      </label>
                      <select
                        name="headerHeight"
                        value={settings.headerHeight || 'medium'}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="small">Small (48px)</option>
                        <option value="medium">Medium (64px)</option>
                        <option value="large">Large (80px)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Brand & Business Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Brand & Business</h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Logo */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Logo
                    </label>
                    <div className="flex items-center space-x-4">
                      {settings.logo && (
                        <img
                          src={settings.logo}
                          alt="Logo preview"
                          className="h-16 w-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                        />
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Logo
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Recommended: Square image, 200x200px or larger
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Logo Size
                    </label>
                    <select
                      name="logoSize"
                      value={settings.logoSize}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="xsmall">Extra Small</option>
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                      <option value="xlarge">Extra Large</option>
                    </select>
                  </div>

                  {/* Logo Indent - Only for standard layout */}
                  {settings.headerLayout === 'standard' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Logo Indent (Standard Layout)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          name="logoIndent"
                          min="0"
                          max="200"
                          step="10"
                          value={settings.logoIndent || 0}
                          onChange={handleInputChange}
                          className="flex-1"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400 w-16 text-right">
                          {settings.logoIndent || 0}px
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Adjust horizontal spacing from the left edge
                      </p>
                    </div>
                  )}
                </div>

                {/* Right Column - Business Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Business Name
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={settings.businessName}
                      onChange={handleInputChange}
                      placeholder="Your Studio Name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tagline
                    </label>
                    <input
                      type="text"
                      name="tagline"
                      value={settings.tagline}
                      onChange={handleInputChange}
                      placeholder="Your inspiring tagline"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Navigation</h3>
              </div>

              <div className="space-y-6">
                {/* Navigation Links */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Navigation Menu
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Toggle visibility and reorder navigation items
                    </p>
                    <div className="space-y-2">
                      {loadingPages ? (
                        <p className="text-sm text-gray-500">Loading pages...</p>
                      ) : pages.length === 0 ? (
                        <p className="text-sm text-gray-500">No pages created yet</p>
                      ) : (
                        pages.map((page, index) => (
                          <div key={page.id} className="flex items-center justify-between bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center space-x-3">
                              {/* Reorder buttons */}
                              <div className="flex flex-col">
                                <button
                                  onClick={() => movePageUp(index)}
                                  disabled={index === 0}
                                  className={`p-0.5 ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                                  title="Move up"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => movePageDown(index)}
                                  disabled={index === pages.length - 1}
                                  className={`p-0.5 ${index === pages.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                                  title="Move down"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white">{page.title}</span>
                              {page.is_home_page && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Home</span>
                              )}
                            </div>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={page.show_in_navigation}
                                onChange={() => togglePageNavigation(page.id, page.show_in_navigation)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                                Show
                              </span>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Navigation Styling */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    Navigation Styling
                  </label>

                  {/* Font and Colors Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Navigation Font */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Font Family
                      </label>
                      <select
                        name="navFont"
                        value={settings.navFont || 'system'}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        style={{ fontFamily: settings.navFont !== 'system' ? settings.navFont : 'inherit' }}
                      >
                        <option value="system" style={{ fontFamily: 'inherit' }}>System Default</option>
                        <optgroup label="Heading Fonts">
                          <option value="Coustard" style={{ fontFamily: 'Coustard' }}>Coustard</option>
                          <option value="Alfa Slab One" style={{ fontFamily: 'Alfa Slab One' }}>Alfa Slab One</option>
                          <option value="Ovo" style={{ fontFamily: 'Ovo' }}>Ovo</option>
                          <option value="Fraunces" style={{ fontFamily: 'Fraunces' }}>Fraunces</option>
                          <option value="Lancelot" style={{ fontFamily: 'Lancelot' }}>Lancelot</option>
                          <option value="Arvo" style={{ fontFamily: 'Arvo' }}>Arvo</option>
                          <option value="Josefin Slab" style={{ fontFamily: 'Josefin Slab' }}>Josefin Slab</option>
                          <option value="Outfit" style={{ fontFamily: 'Outfit' }}>Outfit</option>
                          <option value="Cal Sans" style={{ fontFamily: 'Cal Sans' }}>Cal Sans</option>
                          <option value="Tilt Warp" style={{ fontFamily: 'Tilt Warp' }}>Tilt Warp</option>
                          <option value="McLaren" style={{ fontFamily: 'McLaren' }}>McLaren</option>
                        </optgroup>
                        <optgroup label="Body Fonts">
                          <option value="Mate" style={{ fontFamily: 'Mate' }}>Mate</option>
                          <option value="Buda" style={{ fontFamily: 'Buda' }}>Buda</option>
                          <option value="Bellefair" style={{ fontFamily: 'Bellefair' }}>Bellefair</option>
                          <option value="Questrial" style={{ fontFamily: 'Questrial' }}>Questrial</option>
                          <option value="DM Mono" style={{ fontFamily: 'DM Mono' }}>DM Mono</option>
                          <option value="Quicksand" style={{ fontFamily: 'Quicksand' }}>Quicksand</option>
                          <option value="Nunito Sans" style={{ fontFamily: 'Nunito Sans' }}>Nunito Sans</option>
                          <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                          <option value="Manrope" style={{ fontFamily: 'Manrope' }}>Manrope</option>
                          <option value="Jost" style={{ fontFamily: 'Jost' }}>Jost</option>
                        </optgroup>
                      </select>
                      {settings.navFont && settings.navFont !== 'system' && (
                        <p className="mt-2 text-sm text-gray-500" style={{ fontFamily: settings.navFont }}>
                          Preview: The quick brown fox jumps over the lazy dog
                        </p>
                      )}
                    </div>

                    {/* Navigation Font Color */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Font Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          name="navFontColor"
                          value={settings.navFontColor || '#000000'}
                          onChange={handleInputChange}
                          className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings.navFontColor || '#000000'}
                          onChange={(e) => updateSettings(prev => ({ ...prev, navFontColor: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          placeholder="#000000"
                        />
                      </div>
                    </div>

                    {/* Active Page Indicator Color */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Active Page Indicator Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          name="navActiveIndicatorColor"
                          value={settings.navActiveIndicatorColor || '#3b82f6'}
                          onChange={handleInputChange}
                          className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings.navActiveIndicatorColor || '#3b82f6'}
                          onChange={(e) => updateSettings(prev => ({ ...prev, navActiveIndicatorColor: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          placeholder="#3b82f6"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Color for the underline/indicator on the currently active page
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Social Media</h3>
              </div>

              <div className="space-y-6">
                {/* Social Media Icons */}
                <div>
                  <label className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={settings.showSocialMedia || false}
                      onChange={(e) => updateSettings(prev => ({ ...prev, showSocialMedia: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Show Social Media Icons in Header
                    </span>
                  </label>

                  {settings.showSocialMedia && (
                    <div className="space-y-3 pl-6">
                      {Object.entries(settings.socialMedia || {}).map(([platform, data]) => (
                        <div key={platform} className="flex items-center gap-3">
                          <label className="flex items-center min-w-[120px]">
                            <input
                              type="checkbox"
                              checked={data.enabled}
                              onChange={(e) => {
                                updateSettings(prev => ({
                                  ...prev,
                                  socialMedia: {
                                    ...prev.socialMedia,
                                    [platform]: { ...prev.socialMedia[platform], enabled: e.target.checked }
                                  }
                                }));
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                              {platform}
                            </span>
                          </label>
                          {data.enabled && (
                            <input
                              type="url"
                              value={data.url}
                              onChange={(e) => {
                                updateSettings(prev => ({
                                  ...prev,
                                  socialMedia: {
                                    ...prev.socialMedia,
                                    [platform]: { ...prev.socialMedia[platform], url: e.target.value }
                                  }
                                }));
                              }}
                              placeholder={`https://${platform}.com/yourpage`}
                              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">(displayed in footer)</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={settings.contactEmail}
                    onChange={handleInputChange}
                    placeholder="contact@yourstudio.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={settings.contactPhone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={settings.address}
                    onChange={handleInputChange}
                    placeholder="123 Main St, City, State 12345"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Portal Options Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Portal Options</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <input
                    type="checkbox"
                    name="showPublicClasses"
                    checked={settings.showPublicClasses}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Show classes publicly</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Display your class schedule on the portal</p>
                  </div>
                </label>

                <label className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <input
                    type="checkbox"
                    name="allowPublicEnrollment"
                    checked={settings.allowPublicEnrollment}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Allow public enrollment</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Let visitors enroll in classes online</p>
                  </div>
                </label>

                <label className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <input
                    type="checkbox"
                    name="requireParentInfo"
                    checked={settings.requireParentInfo}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Require parent/guardian info</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Collect guardian details for minors</p>
                  </div>
                </label>

                <label className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <input
                    type="checkbox"
                    name="requireMedicalInfo"
                    checked={settings.requireMedicalInfo}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Require medical/emergency info</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Collect emergency contact and medical details</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          )}

          {/* Site Pages Section */}
          {activeTab === 'pages' && (
          <div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Create and manage pages for your portal. Click on a page to edit its content.
              </p>

              {loadingPages ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7A99D8]"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {/* Existing Pages */}
                  {pages.map((page) => (
                    <div
                      key={page.id}
                      className="group relative bg-gray-100 dark:bg-gray-700 rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-[#7A99D8] transition-colors overflow-hidden"
                    >
                      {/* Page Thumbnail Preview */}
                      <div className="aspect-[4/3] bg-white dark:bg-gray-800 flex items-center justify-center">
                        <div className="text-center p-4">
                          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {page.is_published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>

                      {/* Page Info */}
                      <div className="p-3 border-t border-gray-200 dark:border-gray-600">
                        <h4 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {page.title}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          /{page.slug}
                        </p>
                      </div>

                      {/* Hover Actions */}
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/pages/${page.id}/edit`)}
                          className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                          title="Edit page"
                        >
                          <Pencil className="w-4 h-4 text-gray-700" />
                        </button>
                        {page.slug !== 'home' && (
                          <button
                            onClick={() => handleDeletePage(page.id, page.title)}
                            className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors"
                            title="Delete page"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add New Page Button */}
                  <button
                    onClick={() => setShowNewPageModal(true)}
                    className="aspect-[4/3] bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-[#7A99D8] hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex flex-col items-center justify-center"
                  >
                    <Plus className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Add Page</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          )}

        </div>
      </div>

      {/* New Page Modal */}
      {showNewPageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Create New Page
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Page Title
                </label>
                <input
                  type="text"
                  value={newPageTitle}
                  onChange={handleTitleChange}
                  placeholder="e.g., About Us"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL Slug
                </label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">/</span>
                  <input
                    type="text"
                    value={newPageSlug}
                    onChange={(e) => setNewPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                    placeholder="about-us"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This will be the URL path for the page
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNewPageModal(false);
                  setNewPageTitle('');
                  setNewPageSlug('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePage}
                className="px-4 py-2 text-sm font-medium text-white bg-[#7A99D8] rounded-lg hover:bg-[#6A89C8]"
              >
                Create Page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 mb-4">
                <Upload className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Publish Portal Settings
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to publish these settings? This will make all changes visible to the public on your live portal.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPublishConfirm(false)}
                  disabled={publishing}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {publishing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Publish Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Success Modal */}
      {showPublishSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Portal Settings Published Successfully!
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your portal settings have been published and are now visible to all visitors on your public portal.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPublishSuccess(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Continue Editing
                </button>
                <button
                  onClick={() => window.open(liveUrl, '_blank')}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View Live Portal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Warning Modal */}
      {showNavigationWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-[#3C3C3C] dark:text-white mb-2">
                Unsaved Changes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                You have unsaved changes. Are you sure you want to leave without saving?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleNavigationCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Stay
                </button>
                <button
                  onClick={handleNavigationConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Leave Without Saving
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalSettings;
